import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { aiPairingService } from "../services/aiPairingService";
import "./PairingAnalytics.css";

/**
 * PairingAnalytics Component - Phase 2.2 Implementation
 *
 * Analytics dashboard for restaurants to track pairing performance,
 * popularity metrics, and revenue impact analysis.
 */
const PairingAnalytics = ({
  restaurantId,
  timeRange = "30d",
  onMetricClick = null,
}) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState("popular-pairings");
  const [refreshing, setRefreshing] = useState(false);

  const metricTabs = [
    { id: "popular-pairings", label: "📈 Popular Pairings", icon: "🍽️" },
    { id: "guest-feedback", label: "⭐ Guest Feedback", icon: "💬" },
    { id: "seasonal-trends", label: "📊 Seasonal Trends", icon: "🗓️" },
    { id: "revenue-impact", label: "💰 Revenue Impact", icon: "📈" },
  ];

  useEffect(() => {
    if (restaurantId) {
      loadAnalytics();
    }
  }, [restaurantId, timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Simulate analytics data (in real implementation, this would come from backend)
      const mockAnalytics = await generateMockAnalytics();

      // Get real pairing performance data
      const pairingPerformance = await aiPairingService.getPairingAnalytics(
        restaurantId,
        { timeRange, includeRevenueData: true }
      );

      const combinedAnalytics = {
        ...mockAnalytics,
        pairingPerformance,
        timeRange,
        lastUpdated: new Date().toISOString(),
      };

      setAnalytics(combinedAnalytics);
    } catch (err) {
      console.error("Error loading pairing analytics:", err);
      setError("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const generateMockAnalytics = async () => {
    // Mock data generation for demonstration
    return {
      popularPairings: [
        {
          id: 1,
          beverage: "Cabernet Sauvignon Reserve",
          food: "Grilled Ribeye Steak",
          orders: 156,
          satisfaction: 4.8,
          revenueContribution: 2340,
          trend: "+15%",
        },
        {
          id: 2,
          beverage: "Pinot Grigio",
          food: "Pan-Seared Halibut",
          orders: 142,
          satisfaction: 4.6,
          revenueContribution: 1890,
          trend: "+22%",
        },
        {
          id: 3,
          beverage: "Old Fashioned",
          food: "Smoked Brisket",
          orders: 134,
          satisfaction: 4.7,
          revenueContribution: 2010,
          trend: "+8%",
        },
        {
          id: 4,
          beverage: "Champagne Brut",
          food: "Oysters Rockefeller",
          orders: 98,
          satisfaction: 4.9,
          revenueContribution: 1750,
          trend: "+31%",
        },
      ],
      guestFeedback: {
        averageRating: 4.7,
        totalReviews: 1247,
        sentimentBreakdown: {
          positive: 78,
          neutral: 18,
          negative: 4,
        },
        commonKeywords: [
          "perfect",
          "amazing",
          "excellent match",
          "surprising",
          "delicious",
        ],
      },
      seasonalTrends: {
        currentSeason: "Summer",
        trendingPairings: [
          "Rosé + Grilled Salmon",
          "Light Beer + Fish Tacos",
          "Sangria + Tapas Platter",
          "Aperol Spritz + Bruschetta",
        ],
        seasonalGrowth: "+18%",
      },
      revenueImpact: {
        totalPairingRevenue: 45780,
        averageOrderIncrease: 23,
        conversionRate: 67,
        profitMarginImprovement: 15,
      },
    };
  };

  const refreshAnalytics = async () => {
    setRefreshing(true);
    await loadAnalytics();
    setRefreshing(false);
  };

  const handleMetricSelect = (metricId) => {
    setSelectedMetric(metricId);
    if (onMetricClick) {
      onMetricClick(metricId);
    }
  };

  const renderPopularPairings = () => (
    <div className="analytics-content">
      <div className="content-header">
        <h3>Most Popular Pairings</h3>
        <p>Top performing food-beverage combinations in the last {timeRange}</p>
      </div>

      <div className="pairings-table">
        <div className="table-header">
          <span>Pairing</span>
          <span>Orders</span>
          <span>Rating</span>
          <span>Revenue</span>
          <span>Trend</span>
        </div>

        {analytics.popularPairings.map((pairing) => (
          <div key={pairing.id} className="table-row">
            <div className="pairing-info">
              <span className="beverage-name">{pairing.beverage}</span>
              <span className="food-name">+ {pairing.food}</span>
            </div>
            <span className="orders-count">{pairing.orders}</span>
            <div className="rating-display">
              <span className="rating-value">{pairing.satisfaction}</span>
              <div className="stars">
                {"★".repeat(Math.floor(pairing.satisfaction))}
              </div>
            </div>
            <span className="revenue-amount">
              ${pairing.revenueContribution}
            </span>
            <span
              className={`trend ${
                pairing.trend.startsWith("+") ? "positive" : "negative"
              }`}
            >
              {pairing.trend}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderGuestFeedback = () => (
    <div className="analytics-content">
      <div className="content-header">
        <h3>Guest Feedback Analysis</h3>
        <p>Customer satisfaction and sentiment analysis for pairings</p>
      </div>

      <div className="feedback-metrics">
        <div className="metric-card">
          <h4>Average Rating</h4>
          <div className="large-metric">
            <span className="metric-value">
              {analytics.guestFeedback.averageRating}
            </span>
            <div className="metric-stars">
              {"★".repeat(Math.floor(analytics.guestFeedback.averageRating))}
            </div>
          </div>
          <p className="metric-subtitle">
            {analytics.guestFeedback.totalReviews} total reviews
          </p>
        </div>

        <div className="metric-card">
          <h4>Sentiment Breakdown</h4>
          <div className="sentiment-chart">
            <div
              className="sentiment-bar positive"
              style={{
                width: `${analytics.guestFeedback.sentimentBreakdown.positive}%`,
              }}
            >
              <span>
                {analytics.guestFeedback.sentimentBreakdown.positive}% Positive
              </span>
            </div>
            <div
              className="sentiment-bar neutral"
              style={{
                width: `${analytics.guestFeedback.sentimentBreakdown.neutral}%`,
              }}
            >
              <span>
                {analytics.guestFeedback.sentimentBreakdown.neutral}% Neutral
              </span>
            </div>
            <div
              className="sentiment-bar negative"
              style={{
                width: `${analytics.guestFeedback.sentimentBreakdown.negative}%`,
              }}
            >
              <span>
                {analytics.guestFeedback.sentimentBreakdown.negative}% Negative
              </span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <h4>Common Keywords</h4>
          <div className="keyword-cloud">
            {analytics.guestFeedback.commonKeywords.map((keyword) => (
              <span key={keyword} className="keyword-tag">
                {keyword}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSeasonalTrends = () => (
    <div className="analytics-content">
      <div className="content-header">
        <h3>Seasonal Trends</h3>
        <p>Current season: {analytics.seasonalTrends.currentSeason}</p>
      </div>

      <div className="trends-content">
        <div className="trend-metric">
          <h4>Seasonal Growth</h4>
          <div className="large-metric">
            <span className="metric-value positive">
              {analytics.seasonalTrends.seasonalGrowth}
            </span>
          </div>
          <p className="metric-subtitle">vs. last season</p>
        </div>

        <div className="trending-pairings">
          <h4>Trending This Season</h4>
          <div className="trending-list">
            {analytics.seasonalTrends.trendingPairings.map((pairing, index) => (
              <div key={`${pairing}-${index}`} className="trending-item">
                <span className="trend-rank">#{index + 1}</span>
                <span className="trend-pairing">{pairing}</span>
                <span className="trend-indicator">📈</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRevenueImpact = () => (
    <div className="analytics-content">
      <div className="content-header">
        <h3>Revenue Impact</h3>
        <p>Financial performance of AI-powered pairing recommendations</p>
      </div>

      <div className="revenue-metrics">
        <div className="metric-card">
          <h4>Total Pairing Revenue</h4>
          <div className="large-metric">
            <span className="metric-value">
              ${analytics.revenueImpact.totalPairingRevenue.toLocaleString()}
            </span>
          </div>
          <p className="metric-subtitle">Last {timeRange}</p>
        </div>

        <div className="metric-card">
          <h4>Average Order Increase</h4>
          <div className="large-metric">
            <span className="metric-value positive">
              +{analytics.revenueImpact.averageOrderIncrease}%
            </span>
          </div>
          <p className="metric-subtitle">With pairings vs. without</p>
        </div>

        <div className="metric-card">
          <h4>Conversion Rate</h4>
          <div className="large-metric">
            <span className="metric-value">
              {analytics.revenueImpact.conversionRate}%
            </span>
          </div>
          <p className="metric-subtitle">Guests who order suggested pairings</p>
        </div>

        <div className="metric-card">
          <h4>Profit Margin Improvement</h4>
          <div className="large-metric">
            <span className="metric-value positive">
              +{analytics.revenueImpact.profitMarginImprovement}%
            </span>
          </div>
          <p className="metric-subtitle">Through premium pairing selections</p>
        </div>
      </div>
    </div>
  );

  const renderSelectedMetric = () => {
    if (!analytics) return null;

    switch (selectedMetric) {
      case "popular-pairings":
        return renderPopularPairings();
      case "guest-feedback":
        return renderGuestFeedback();
      case "seasonal-trends":
        return renderSeasonalTrends();
      case "revenue-impact":
        return renderRevenueImpact();
      default:
        return renderPopularPairings();
    }
  };

  if (loading) {
    return (
      <div className="pairing-analytics">
        <div className="analytics-header">
          <h2>📊 Pairing Analytics Dashboard</h2>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pairing-analytics">
        <div className="analytics-header">
          <h2>📊 Pairing Analytics Dashboard</h2>
        </div>
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button onClick={loadAnalytics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pairing-analytics">
      <div className="analytics-header">
        <div className="header-content">
          <h2>📊 Pairing Analytics Dashboard</h2>
          <p className="header-subtitle">
            Insights and performance metrics for your AI-powered pairing system
          </p>
        </div>
        <div className="header-actions">
          <button
            onClick={refreshAnalytics}
            className={`refresh-button ${refreshing ? "refreshing" : ""}`}
            disabled={refreshing}
          >
            🔄 {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="analytics-tabs">
        {metricTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleMetricSelect(tab.id)}
            className={`metric-tab ${
              selectedMetric === tab.id ? "active" : ""
            }`}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="analytics-body">{renderSelectedMetric()}</div>

      {analytics.lastUpdated && (
        <div className="analytics-footer">
          <p className="last-updated">
            Last updated: {new Date(analytics.lastUpdated).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

PairingAnalytics.propTypes = {
  restaurantId: PropTypes.string.isRequired,
  timeRange: PropTypes.oneOf(["7d", "30d", "90d", "1y"]),
  onMetricClick: PropTypes.func,
};

PairingAnalytics.defaultProps = {
  timeRange: "30d",
  onMetricClick: null,
};

export default PairingAnalytics;
